<template>
  <div class="h-screen overflow-hidden flex flex-col bg-white border-solid border-l border-0 border-gray-border">
    <h2 class="py-8 pl-8 font-semibold border-solid border-0 border-b border-gray-border">Create New Bot</h2>

    <form @submit="createBot" class="flex flex-col h-full justify-between pt-6">
      <div class="flex flex-col">
        <div class="px-8 pb-7">
          <base-label id="botName" label="Name"/>
          <base-input id="botName"
                      name="botName"
                      variant="editor"
                      type="string"
                      class="w-full border-gray py-px mt-1 h-10"
                      v-model.trim="botName"
          />
        </div>

        <span v-if="showUnreleased" class="text-black font-semibold border-solid border-0 border-t border-gray-border pt-6 pl-8">
          PRIMARY MARKET
        </span>
        <div v-if="showUnreleased" class="px-8 mt-2">
          <base-label id="botMarketName" label="Name"/>
          <base-input id="botMarketName"
                      name="botMarketName"
                      variant="editor"
                      type="string"
                      class="w-full border-gray py-px mt-1 h-10"
                      v-model.trim="botMarketName"
          />
        </div>
        <div class="grid grid-cols-2 gap-2 px-8 pb-7 mt-4" :class="{ 'mt-4': showUnreleased }">
          <div class="col-span-1">
            <base-label id="botLanguage" label="Language"/>
            <queryable-select id="language"
                              :value="botLanguage"
                              :options="languageOptions"
                              class="w-full border-gray mt-1 h-10"
                              @input="botLanguage = $event"
            />
          </div>

          <div class="col-span-1">
            <base-label id="botTimezone" label="Timezone"/>
            <queryable-select id="timezone"
                              :value="botTimezone"
                              :options="timezoneOptions"
                              class="w-full border-gray mt-1 h-10"
                              @input="botTimezone = $event"
            />
          </div>
        </div>

        <div class="text-gray-dark border-solid border-0 border-t border-gray-border pt-6 px-8">
          <base-label id="createFromType" label="Create from"/>
          <div class="flex mt-2">
            <base-radio v-for="(createFromType, i) of createFromOptions"
                        :key="i"
                        :id="`createFromType${i}`"
                        :value="createFromType"
                        :name="'createFromType'"
                        :class="`ml-${i === 0 ? 0 : 10}`"
                        v-model="selectedCreateFrom"
            >
              {{ createFromType }}
            </base-radio>
          </div>
        </div>

        <!-- <div v-if="selectedCreateFrom === CREATE_FROM_TYPES.EMPTY" class="mt-4">
          <base-label id="createFromEmpty" label="Type"/>
          <base-select :value="botType"
                      :options="botTypeOptions"
                      class="w-full border-gray mt-1 h-10"
                      @input="botType = $event"
          />
        </div> -->
        <settings-bot-create-from-existing v-if="selectedCreateFrom === CREATE_FROM_TYPES.TEMPLATE"
                                           :create-from-type="CREATE_FROM_TYPES.TEMPLATE"
                                           :selected-option-id="selectedBotTemplateId"
                                           :options="botTemplates"
                                           :should-clone-variables="shouldCloneVariables"
                                           :is-prefix-from-input-disabled="true"
                                           :prefix-from="prefixFrom"
                                           :prefix-to="prefixTo"
                                           @on-checked="onChecked"
                                           @update-prefix-to="updatePrefixTo"
                                           @select-option="selectBotTemplate"
                                           class="px-8 mt-4"
        />
        <settings-bot-create-from-existing v-if="selectedCreateFrom === CREATE_FROM_TYPES.CLONE"
                                           :create-from-type="CREATE_FROM_TYPES.CLONE + ' from'"
                                           :selected-option-id="selectedBotId"
                                           :options="botList"
                                           :should-clone-variables="shouldCloneVariables"
                                           :prefix-from="prefixFrom"
                                           :prefix-to="prefixTo"
                                           @on-checked="onChecked"
                                           @update-prefix-from="updatePrefixFrom"
                                           @update-prefix-to="updatePrefixTo"
                                           @select-option="selectBotClone"
                                           class="px-8 mt-4"
        />
      </div>

      <busy-button id="createButton"
                   :variant="createButtonVariant"
                   class="w-1/2 mb-10 ml-8"
                   :is-loading="isLoading"
                   :is-disabled="isLoading"
                   @click="createBot"
      >
        <span class="w-full">{{ createButtonText }}</span>
      </busy-button>

    </form>
  </div>
</template>

<script>
import BusyButton from '@/components/common/BusyButton'
import QueryableSelect from '@/components/common/QueryableSelect.vue'
import SettingsBotCreateFromExisting from '@/components/settings/bots/SettingsBotCreateFromExisting.vue'
import { BOT_TYPES, BOT_LANGUAGES, BOT_TIMEZONES } from '@/utils/bot.constants'
import { SAVE_BUTTON_VARIANTS } from '@/utils/editor.constants'
import { CREATE_BUTTON_LABELS } from '@/utils/text.constants'
import botModule from '@/store/modules/bot.module'
import botService from '@/services/amio/bot.service'
import multiBotService from '@/services/amio/multi-bot.service'
import botTemplatesService from '@/services/amio/bot-templates.service'
import toaster from '@/utils/toaster'
import head from 'ramda/src/head'
import isEmpty from 'ramda/src/isEmpty'
import previewModule from '@/store/modules/preview.module'

const CREATE_FROM_TYPES = {
  // EMPTY: 'Empty',
  TEMPLATE: 'Template',
  CLONE: 'Clone'
}

export default {
  name: 'SettingsBotCreate',
  components: { BusyButton, QueryableSelect, SettingsBotCreateFromExisting },
  data() {
    return {
      botType: BOT_TYPES.CHAT,
      botName: '',
      botLanguage: '',
      botTimezone: 'Europe/Prague',
      botTemplates: [],
      botMarketName: '',
      isLoading: false,
      selectedCreateFrom: CREATE_FROM_TYPES.TEMPLATE,
      selectedBotTemplateId: '',
      selectedBotId: '',
      shouldCloneVariables: false,
      prefixFrom: '',
      prefixTo: '',
      CREATE_FROM_TYPES,
      createButtonVariant: SAVE_BUTTON_VARIANTS.DEFAULT,
      createButtonText: CREATE_BUTTON_LABELS.DEFAULT
    }
  },
  computed: {
    botList: botModule.getBotList,
    error: previewModule.error,

    createFromOptions() {
      return isEmpty(this.botList) ? Object.values(CREATE_FROM_TYPES).filter(type => type !== CREATE_FROM_TYPES.CLONE) : Object.values(CREATE_FROM_TYPES)
    },
    botTypeOptions() {
      return Object.values(BOT_TYPES).map((botType) => ({
        text: botType[0].toUpperCase() + botType.slice(1),
        value: botType
      }))
    },
    languageOptions() {
      return BOT_LANGUAGES
    },
    languageLabel() {
      return this.botLanguage ? this.languageOptions.find((language) => language.value === this.botLanguage).text : '-'
    },
    timezoneOptions() {
      return BOT_TIMEZONES.map((timezone) => ({
        text: timezone,
        value: timezone
      }))
    },
    shouldTranslateBot() {
      if (!this.botLanguage) return false
      if (this.botTemplateLanguage) return this.botTemplateLanguage !== this.botLanguage
      if (this.botCloneLanguage) return this.botCloneLanguage !== this.botLanguage

      return false
    },
    botTemplateLanguage() {
      return this.botTemplates.find(t => t.id === this.selectedBotTemplateId)?.botLanguage
    },
    botCloneLanguage() {
      return this.botList.find(b => b.id === this.selectedBotId)?.language
    }
  },
  methods: {
    selectBotTemplate(botTemplateId) {
      const botTemplate = this.botTemplates.find((template) => template.id === botTemplateId)
      this.selectedBotTemplateId = botTemplate.id
      this.prefixFrom = botTemplate.variablesPrefix
      this.botType = botTemplate.botType
      if (!this.botLanguage) this.botLanguage = botTemplate.botLanguage
    },
    selectBotClone(botId) {
      const bot = this.botList.find((bot) => bot.id === botId)
      if (this.selectedBotTemplateId && !this.selectedBotId) this.prefixFrom = ''
      this.selectedBotId = bot.id
      this.botType = bot.type
      if (!this.botLanguage) this.botLanguage = bot.language
    },
    onChecked(shouldCloneVariables) {
      if (shouldCloneVariables) this.prefixTo = this.botLanguage + '-'
      this.shouldCloneVariables = shouldCloneVariables
    },
    updatePrefixFrom(prefixFrom) {
      this.prefixFrom = prefixFrom
    },
    updatePrefixTo(prefixTo) {
      this.prefixTo = prefixTo
    },
    resolveTranslateParams() {
      return this.shouldTranslateBot ? {
        from_language: this.selectedCreateFrom === CREATE_FROM_TYPES.TEMPLATE ? this.botTemplateLanguage : this.botCloneLanguage,
        to_language: this.botLanguage
      } : null
    },
    resolveCloneVariablesParams() {
      return this.shouldCloneVariables ? {
        prefix_original: this.prefixFrom,
        prefix_converted: this.prefixTo
      } : null
    },
    async createBot() {
      this.isLoading = true
      try {
        const translateParams = this.resolveTranslateParams()
        const cloneVariablesParams = this.resolveCloneVariablesParams()
        if (this.selectedCreateFrom === CREATE_FROM_TYPES.TEMPLATE) {
          const variantName = `${this.botMarketName} - ${this.botLanguage.toUpperCase()}`
          const newBotFromTemplate = this.showUnreleased ?
            await multiBotService.createFromTemplate(this.botName, this.selectedBotTemplateId, variantName, this.botTimezone, this.botType, this.botLanguage) :
            await botService.createFromTemplate(this.botName, this.botTimezone, this.botType, this.botLanguage, this.selectedBotTemplateId, translateParams, cloneVariablesParams)
          const newBot = this.showUnreleased && newBotFromTemplate.primaryBot ? newBotFromTemplate.primaryBot : newBotFromTemplate
          newBot.is_primary_variant = true
          this.$emit('process-new-bot', newBot)
        } else if (this.selectedCreateFrom === CREATE_FROM_TYPES.CLONE) {
          const newBotFromClone = await botService.createFromClone(this.botName, this.botTimezone, this.botLanguage, this.selectedBotId, translateParams, cloneVariablesParams)
          this.$emit('process-new-bot', newBotFromClone)
        }
        toaster.info('Creating a new bot, this might take up to 1 minute.')
      } catch (err) {
        const data = err.response?.data
        const firstError = head(data?.details.errors)
        const errorMessage = firstError?.message || err.message

        if (errorMessage.startsWith('These variables are already defined')) {
          const callToAction = '<br/>Either change the "New variables prefix" or <a href="/settings/variables" target="_blank">delete the above variables.</a>'
          toaster.error(errorMessage, err, {}, null, callToAction)
        } else if(errorMessage === 'Prefixes cannot be the same' || errorMessage.startsWith('Variables')) {
          this.error = { description: errorMessage }
        } else if(errorMessage.startsWith('Maximum workspaces')) {
          toaster.error('Failed to create new bot, please contact ', err, {}, null, '<a href="mailto:support@amio.io?subject=Error%3A%20Create%20New%20Bot">support@amio.io</a>')
        } else if(data?.code === 'generic' && errorMessage.includes('prefix')) {
          this.error = firstError
        } else {
          toaster.error(errorMessage)
        }
      } finally {
        this.isLoading = false
        this.startButtonStateTimeout()
      }
    },

    startButtonStateTimeout() {
      this.setButtonProps(this.error ? 'ERROR' : 'SUCCESS')
      this.buttonStateTimeout = setTimeout(() => {
        this.setButtonProps()
      }, 8000)
    },

    setButtonProps(buttonState = 'DEFAULT') {
      this.createButtonVariant = SAVE_BUTTON_VARIANTS[buttonState]
      this.createButtonText = CREATE_BUTTON_LABELS[buttonState]
    }
  },
  async mounted() {
    this.botTemplates = await botTemplatesService.list()
    this.error = null
  }
}
</script>
